import React, { useState } from 'react';
import { Pill } from '../ui/pill';
import { Card } from '../ui/card';
import { Input } from '../ui/input';
import {
  FileText, Search, Languages, Menu, X,
  BookOpen, Rocket, Bluetooth, Code, Database, Monitor,
  ArrowRight, Sparkles, Cpu, Wifi,
} from 'lucide-react';
import { useLanguageStore } from '../../stores/languageStore';
import { useTranslation } from '../../locales';
import { DocumentsSidebar } from './DocumentsSidebar';
import { DocumentsContent } from './DocumentsContent';
import { getDocumentStructure } from '../../docs/structure';

export type DocumentSection = string;
export type DocumentSubsection = string;

type SectionDescKey = 'overview' | 'gettingStarted' | 'bleProtocol' | 'apiReference' | 'dataGuide' | 'interface';

const sectionMeta: Record<string, { icon: React.ComponentType<{ className?: string; strokeWidth?: number }>; descKey: SectionDescKey; gradient: string }> = {
  'overview':        { icon: BookOpen,   descKey: 'overview',        gradient: 'from-indigo-500/20 to-purple-500/10' },
  'getting-started': { icon: Rocket,     descKey: 'gettingStarted',  gradient: 'from-emerald-500/20 to-teal-500/10' },
  'ble-protocol':    { icon: Bluetooth,  descKey: 'bleProtocol',     gradient: 'from-blue-500/20 to-cyan-500/10' },
  'api-reference':   { icon: Code,       descKey: 'apiReference',    gradient: 'from-amber-500/20 to-orange-500/10' },
  'data-guide':      { icon: Database,   descKey: 'dataGuide',       gradient: 'from-rose-500/20 to-pink-500/10' },
  'interface':       { icon: Monitor,    descKey: 'interface',       gradient: 'from-violet-500/20 to-fuchsia-500/10' },
};

const Documents: React.FC = () => {
  const { currentLanguage, setLanguage } = useLanguageStore();
  const t = useTranslation(currentLanguage);
  const documentStructure = getDocumentStructure(currentLanguage);

  const [activeSection, setActiveSection] = useState<DocumentSection>('overview');
  const [activeSubsection, setActiveSubsection] = useState<DocumentSubsection>('overview');
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showLanding, setShowLanding] = useState(true);

  const handleLanguageToggle = () => {
    setLanguage(currentLanguage === 'ko' ? 'en' : 'ko');
  };

  const handleNavigate = (section: string, subsection: string) => {
    setActiveSection(section as DocumentSection);
    setActiveSubsection(subsection);
    setShowLanding(false);
    setSidebarOpen(false);
  };

  const handleSectionChange = (section: DocumentSection) => {
    setActiveSection(section);
    // Auto-select first subsection
    const sec = documentStructure.find(s => s.id === section);
    if (sec && sec.subsections.length > 0) {
      setActiveSubsection(sec.subsections[0].id);
    }
    setShowLanding(false);
    setSidebarOpen(false);
  };

  const handleSubsectionChange = (subsection: DocumentSubsection) => {
    setActiveSubsection(subsection);
    setShowLanding(false);
    setSidebarOpen(false);
  };

  return (
    <div className="h-full flex bg-background">
      {/* ── Sidebar (desktop) ── */}
      <div className="hidden md:block w-64 flex-shrink-0 border-r border-white/6 bg-white/[0.02]">
        <DocumentsSidebar
          activeSection={activeSection}
          activeSubsection={activeSubsection}
          onSectionChange={handleSectionChange}
          onSubsectionChange={handleSubsectionChange}
        />
      </div>

      {/* ── Sidebar overlay (mobile) ── */}
      {sidebarOpen && (
        <div className="md:hidden fixed inset-0 z-40" onClick={() => setSidebarOpen(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div
            className="absolute left-0 top-0 bottom-0 w-72 max-w-[80vw] border-r border-white/6 bg-[#0b0b14] z-50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-3 border-b border-white/6">
              <span className="lb-eyebrow">{t.documents.contents}</span>
              <button onClick={() => setSidebarOpen(false)} className="p-1 text-slate-400 hover:text-white">
                <X className="w-5 h-5" strokeWidth={1.5} />
              </button>
            </div>
            <DocumentsSidebar
              activeSection={activeSection}
              activeSubsection={activeSubsection}
              onSectionChange={handleSectionChange}
              onSubsectionChange={handleSubsectionChange}
            />
          </div>
        </div>
      )}

      {/* ── Main ── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="flex-shrink-0 border-b border-white/6 bg-white/[0.02] p-4 md:p-6">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setSidebarOpen(true)}
                className="md:hidden p-2 -ml-2 text-slate-300 hover:text-white"
                aria-label={t.documents.menuLabel}
              >
                <Menu className="w-5 h-5" strokeWidth={1.5} />
              </button>
              <FileText className="w-8 h-8 text-indigo-400 hidden sm:block" strokeWidth={1.5} />
              <div>
                <h1 className="lb-display-lg">{t.documents.title}</h1>
                <p className="t-soft text-sm">{t.documents.subtitle}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 md:gap-3">
              <div className="relative hidden sm:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" strokeWidth={1.5} />
                <Input
                  placeholder={t.documents.searchPlaceholder}
                  value={searchQuery}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
                  className="pl-10 w-40 md:w-64 bg-white/[0.03] border-white/[0.06] text-white placeholder:text-slate-500"
                />
              </div>
              <Pill variant="secondary" size="sm" onClick={handleLanguageToggle} className="flex items-center gap-2">
                <Languages className="w-4 h-4" strokeWidth={1.5} />
                <span className="hidden sm:inline">{currentLanguage === 'ko' ? t.settings.languageKo : t.settings.languageEn}</span>
              </Pill>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {showLanding ? (
            /* ── Landing / Hero ── */
            <div className="min-h-full">
              {/* Hero */}
              <div className="relative overflow-hidden border-b border-white/6">
                <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/5 via-transparent to-purple-500/5" />
                <div className="relative max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-indigo-500/10 border border-indigo-400/30 rounded-2xl flex items-center justify-center">
                      <Sparkles className="w-6 h-6 text-indigo-400" strokeWidth={1.5} />
                    </div>
                    <span className="lb-eyebrow">{t.documents.sdkDocumentation}</span>
                  </div>
                  <h1 className="lb-display-lg mb-4">
                    LINK BAND SDK
                  </h1>
                  <p className="text-lg t-soft max-w-2xl mb-8">
                    {t.documents.sdkHeroDesc}
                  </p>
                  <div className="flex items-center gap-3 flex-wrap">
                    <div className="flex items-center gap-2 text-sm t-faint bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-2">
                      <Cpu className="w-4 h-4 text-indigo-400" strokeWidth={1.5} />
                      <span>EEG 250Hz</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm t-faint bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-2">
                      <Wifi className="w-4 h-4 text-rose-400" strokeWidth={1.5} />
                      <span>PPG 50Hz</span>
                    </div>
                    <div className="flex items-center gap-2 text-sm t-faint bg-white/[0.03] border border-white/[0.06] rounded-lg px-4 py-2">
                      <Monitor className="w-4 h-4 text-emerald-400" strokeWidth={1.5} />
                      <span>ACC 30Hz</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section Grid */}
              <div className="max-w-6xl mx-auto px-6 md:px-10 py-12 md:py-16">
                <h3 className="font-medium mb-2">{t.documents.browseDocs}</h3>
                <p className="t-soft mb-8">{t.documents.browseDocsDesc}</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {documentStructure.map((section) => {
                    const meta = sectionMeta[section.id];
                    if (!meta) return null;
                    const Icon = meta.icon;
                    return (
                      <Card
                        key={section.id}
                        className="p-5 cursor-pointer group hover:bg-card/90 transition-colors"
                        onClick={() => handleSectionChange(section.id as DocumentSection)}
                      >
                        <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${meta.gradient} flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                          <Icon className="w-5 h-5 text-white/80" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-semibold t-strong mb-1.5">
                          {section.title}
                        </h3>
                        <p className="text-sm t-faint mb-3">
                          {t.documents.sectionDesc[meta.descKey]}
                        </p>
                        <div className="flex items-center gap-1 text-xs text-indigo-400 opacity-0 group-hover:opacity-100 transition-opacity">
                          <span>{t.documents.viewDocs}</span>
                          <ArrowRight className="w-3 h-3" strokeWidth={1.5} />
                        </div>
                      </Card>
                    );
                  })}
                </div>
              </div>
            </div>
          ) : (
            /* ── Document Content View ── */
            <DocumentsContent
              selectedSection={activeSection}
              selectedSubsection={activeSubsection}
              onNavigate={handleNavigate}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Documents;
